import React, { useEffect, useState, useCallback } from 'react';
import { Button, SelectChangeEvent } from '@mui/material';
import { useTranslation } from 'react-i18next';
import useSeriesStore from '@modules/entertainment/pages/series/store/series.store.ts';
import useMediaLibraryStore from '@store/media-library-store/media-library.store.ts';
import AppDialog from '@shared/components/app-dialog/app-dialog.tsx';
import DeleteDialog from '@shared/components/delete-dialog/delete-dialog.tsx';
import TabsComponent from '@shared/components/tabs/tabs.tsx';
import ViewControls from '@components/view-controls/view-controls.tsx';
import PaginationComponent from '@components/pagination/pagination.tsx';
import EpisodeTable from './episode-table/episode-table.tsx';
import emptyIcon from '@assets/icons/empty.svg';
import { ViewControlsEnum } from '@models/view-controls.model.ts';
import { LanguageEnum } from '@models/language.model.ts';
import { EpisodeTableProps } from '@modules/entertainment/models/series.model.ts';
import { episodeColumns } from '@modules/entertainment/pages/series/compnents/series-dialog-content/components/episode-table-container/components/columns/columns.tsx';
import SeasonDialogForm from '@modules/entertainment/pages/series/compnents/series-dialog-content/components/episode-table-container/components/season-dialog/season-dialog.tsx';
import { AddIcon } from '@components/icons/svg-icon.tsx';
import { ICONS } from '@components/icons/icon.tsx';
import EpisodeDialog from '@modules/entertainment/pages/series/compnents/series-dialog-content/components/episode-table-container/components/epsiode-dialog/episode-dialog.tsx';
import { TriggerTypeEnum } from '@models/table.model.ts';

interface EpisodesPagination {
  page: number;
  rowsPerPage: number;
  totalPages: number;
}

const EpisodeTableContainer: React.FC<EpisodeTableProps> = ({
  selectedSerie,
  language,
  setLanguage
}) => {
  const { t } = useTranslation();
  const { resetSelectedMedia } = useMediaLibraryStore();
  const {
    deleteEpisode,
    editEpisode,
    createEpisode,
    deleteSeason,
    createSeason,
    editSeason,
    isLoading,
    isLoaded,
    isSeasonLoading,
    isEpisodeLoading,
    getEpisodes,
    seasonsList,
    episodesData,
    episodesPagination
  } = useSeriesStore();

  const [currentDisplayTab, setCurrentDisplayTab] = useState<string>('');
  const [selectedSeason, setSelectedSeason] = useState<any>(null);
  const [showSeasonDialog, setShowSeasonDialog] = useState(false);
  const [showSeasonDeleteDialog, setShowSeasonDeleteDialog] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [view, setView] = useState(ViewControlsEnum.List);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [pagination, setPagination] = useState<EpisodesPagination>({
    page: 1,
    rowsPerPage: 5,
    totalPages: 0
  });

  const count = selectedRows?.length;

  const handleSelectAll = () => {
    if (selectedRows?.length === episodesData?.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(episodesData.map((row: any) => row._id));
    }
  };

  const handleSelectRow = (rowId: string) => {
    setSelectedRows((prev) => {
      const newSelectedRows = [...prev];
      const index = newSelectedRows.indexOf(rowId);
      if (index === -1) newSelectedRows.push(rowId);
      else newSelectedRows.splice(index, 1);
      return newSelectedRows;
    });
  };

  const handleClose = () => {
    resetSelectedMedia('image');
    setShowSeasonDialog(false);
    setShowDeleteDialog(false);
    setOpenDialog(false);
    setShowSeasonDeleteDialog(false);
    setSelectedRows([]);
    setSelectedSeason(null);
    setSelectedItem(null);
  };

  const handleSeasonDialogOpen = () => setShowSeasonDialog(true);
  const handleEditSeason = (season: any) => {
    setSelectedSeason(season);
    setShowSeasonDialog(true);
  };
  const handleDeleteSeason = (season: any) => {
    setSelectedSeason(season);
    setShowSeasonDeleteDialog(true);
  };

  const handleSeasonDeleteConfirm = async () => {
    if (!selectedSeason) return;
    deleteSeason({
      id: selectedSeason.id,
      seriesId: selectedSerie?._id,
      onSuccess: () => {
        const updatedSeasonsList =
          seasonsList?.filter((s) => s.value !== selectedSeason.id) || [];
        if (!updatedSeasonsList.length) setCurrentDisplayTab('');
        else if (currentDisplayTab === selectedSeason.id)
          setCurrentDisplayTab(updatedSeasonsList[0].value);
        setShowSeasonDeleteDialog(false);
      }
    });
  };

  const handleEdit = (row: any) => {
    setOpenDialog(true);
    setSelectedItem(row);
  };

  const handleDelete = (row: any) => {
    setShowDeleteDialog(true);
    setSelectedItem(row);
  };

  const handleTableTrigger = (type: TriggerTypeEnum) => {
    if (type === TriggerTypeEnum.Add) {
      setOpenDialog(true);
    }
  };

  const handleAction = (action: { type: string; value?: string }) => {
    switch (action.type) {
      case ViewControlsEnum.View:
        setView(action.value as ViewControlsEnum);
        break;
      case ViewControlsEnum.Delete:
        handleDelete(selectedRows);
        break;
      case ViewControlsEnum.Language:
        setLanguage(action.value as LanguageEnum);
        break;
    }
  };

  const handleSave = async (row: any) => {
    if (!currentDisplayTab) return;

    const rowWithSeason = { ...row, seasonId: currentDisplayTab };

    if (selectedItem?._id) {
      editEpisode({
        data: rowWithSeason,
        id: selectedItem._id,
        seasonId: currentDisplayTab,
        page: pagination.page,
        limit: pagination.rowsPerPage,
        handleClose
      });
    } else {
      createEpisode({
        data: rowWithSeason,
        page: pagination.page,
        seasonId: currentDisplayTab,
        limit: pagination.rowsPerPage,
        handleClose
      });
    }
  };

  const handleDeleteConfirm = () => {
    deleteEpisode({
      id: selectedItem?._id ? selectedItem?._id : selectedRows,
      seasonId: currentDisplayTab,
      page: pagination.page,
      limit: pagination.rowsPerPage,
      handleClose
    });
  };

  const handleSaveSeason = async (data: any) => {
    const seasonData = data;
    seasonData.seriesId = selectedSerie?._id;

    if (selectedSeason) {
      editSeason({
        data: seasonData,
        id: selectedSeason?.id,
        seriesId: selectedSerie?._id,
        handleClose: () => setShowSeasonDialog(false)
      });
    } else {
      createSeason({
        data: seasonData,
        id: selectedSerie?._id,
        handleClose: () => setShowSeasonDialog(false)
      });
    }
  };

  const handleChangeSeason = (_event: any, newValue: string) => {
    if (newValue === currentDisplayTab) return;
    setCurrentDisplayTab(newValue);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const fetchEpisodesForSeason = useCallback(
    (seasonId: string, page = 1, rowsPerPage = 5) => {
      if (!seasonId) return;
      getEpisodes({
        filters: { season: [seasonId] },
        page,
        limit: rowsPerPage
      });
    },
    [getEpisodes]
  );

  const handlePageChange = (_event: any, value: number) => {
    setPagination((prev) => ({ ...prev, page: value }));
    fetchEpisodesForSeason(currentDisplayTab, value, pagination.rowsPerPage);
  };

  const handleRowsPerPageChange = (event: SelectChangeEvent<number>) => {
    const newRows = Number(event.target.value);

    setPagination((prev) => ({ ...prev, page: 1, rowsPerPage: newRows }));

    fetchEpisodesForSeason(currentDisplayTab, 1, newRows);
  };

  const columns = episodeColumns(
    handleEdit,
    handleDelete,
    handleSelectRow,
    handleSelectAll,
    selectedRows,
    episodesData,
    language
  );

  useEffect(() => {
    if (episodesPagination) {
      setPagination(episodesPagination);
    }
  }, [episodesPagination]);

  useEffect(() => {
    if (seasonsList?.length && !currentDisplayTab) {
      setCurrentDisplayTab(seasonsList[0].value);
    }
  }, [seasonsList]);

  useEffect(() => {
    if (currentDisplayTab) {
      fetchEpisodesForSeason(
        currentDisplayTab,
        pagination.page,
        pagination.rowsPerPage
      );
    }
  }, [currentDisplayTab]);

  return (
    <div className="w-full h-full">
      <div className="flex justify-between mb-7 mt-7">
        <div className="max-w-[85%] overflow-x-auto flex items-center gap-2">
          <TabsComponent
            tabs={seasonsList || []}
            value={currentDisplayTab || seasonsList?.[0]?.value}
            onChange={handleChangeSeason}
            onDelete={handleDeleteSeason}
            onEdit={handleEditSeason}
          />
          <div
            onClick={handleSeasonDialogOpen}
            className="font-medium flex items-center gap-2 text-[color:var(--blue-4)] cursor-pointer hover:text-[color:var(--primary)]"
          >
            <AddIcon />
            {t('entertainment_add_season')}
          </div>
        </div>

        <Button
          variant="contained"
          color="primary"
          startIcon={ICONS.plusIcon}
          onClick={() => handleEdit(selectedItem)}
          disabled={!seasonsList?.length}
        >
          {t('add')}
        </Button>
      </div>

      <ViewControls onAction={handleAction} selectedRows={selectedRows} />

      <div className="w-full h-full">
        {currentDisplayTab && view === ViewControlsEnum.List ? (
          <div>
            <EpisodeTable
              columns={columns}
              tableData={episodesData}
              onTriggerAction={handleTableTrigger}
              isLoading={isLoading()}
              isLoaded={isLoaded()}
            />

            {episodesData?.length > 0 && (
              <PaginationComponent
                count={pagination.totalPages}
                page={pagination.page}
                rowsPerPage={pagination.rowsPerPage}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            )}
          </div>
        ) : (
          <div className="flex flex-col justify-center items-center py-9 gap-6 max-lg-xl:gap-5">
            <img
              src={emptyIcon}
              alt="Setup"
              className="max-lg-xl:w-[110px] max-h-[300px]"
            />
            <p className="text-[var(--blue-4)] text-[30px] opacity-40 font-semibold text-center max-lg-xl:text-[15px]">
              {t('empty_content_text_serie')}
            </p>
          </div>
        )}
      </div>

      <AppDialog
        open={openDialog}
        title={t('entertainment_episode_title')}
        size="xl"
        content={
          <EpisodeDialog selectedItem={selectedItem} onSave={handleSave} />
        }
        onClose={handleClose}
      />

      <DeleteDialog
        onCancel={handleClose}
        onDelete={handleDeleteConfirm}
        open={showDeleteDialog}
        isLoading={isEpisodeLoading()}
        title={
          selectedItem?._id
            ? t('delete_popup_title')
            : t('delete_popup_title_many_items', { count })
        }
      />

      <DeleteDialog
        onCancel={handleClose}
        onDelete={handleSeasonDeleteConfirm}
        open={showSeasonDeleteDialog}
        isLoading={isSeasonLoading()}
        title={t('delete_popup_title')}
      />

      <AppDialog
        open={showSeasonDialog}
        title={t('season')}
        size="md"
        content={
          <SeasonDialogForm
            handleClose={handleClose}
            onSave={handleSaveSeason}
            selectedSeason={selectedSeason}
          />
        }
        onClose={handleClose}
      />
    </div>
  );
};

export default EpisodeTableContainer;
